<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import PageBreadcrumb from '../../components/PageBreadcrumb.vue';
import AppFooter from '../../components/AppFooter.vue';
import { useSourcesStore } from '../../stores/sources.store';
import { useDestinationsStore } from '../../stores/destinations.store';
import { usePipelinesStore } from '../../stores/pipelines.store';
import { useTransformsStore } from '../../stores/transforms.store';
import { useRouter, onBeforeRouteLeave } from 'vue-router';
import TreeView from '../../components/TreeView.vue';
import ErrorMessage from '../../components/ErrorMessage.vue';
import { AlertTriangle, Database, ChevronRight, CloudDownload, CloudUpload, LoaderCircle } from 'lucide-vue-next';
import { HelpCircle, Search } from 'lucide-vue-next';
import _ from 'lodash';
import { PlusCircle } from 'lucide-vue-next';
import SearchInput from '@/components/global/SearchInput.vue';
import { buildTreeNodesOutOfExistingConfig } from '../../utils/utils';
import { getConnectorIcon, getConnectorDisplayName, TAG_TYPES, MAX_ONE_TAG_PER_TYPE } from '@/utils/constants';
import ActionMenu from '@/components/ActionMenu.vue';
import { useTagsStore } from '@/stores/tags.store';
import { MultiSelect } from '@/components/ui/multi-select';
import { toast } from '@/composables/useToast';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Stepper, StepperItem, StepperTrigger, StepperIndicator, StepperTitle, StepperSeparator } from '@/components/ui/stepper';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';

let router = useRouter();
const sourcesStore = useSourcesStore();
const destinationsStore = useDestinationsStore();
const pipelinesStore = usePipelinesStore();
const transformsStore = useTransformsStore();
const tagsStore = useTagsStore();

let sources = ref(_.cloneDeep(sourcesStore.allBrief));
let destinations = ref(_.cloneDeep(destinationsStore.allBrief));

let formError = ref('');

let tagsLoading = ref(false);
let tags = ref([]);
let selectedTags = ref([]);

const selectedTagIds = computed({
    get() {
        return selectedTags.value.map((tag) => tag.id);
    },
    set(ids) {
        selectedTags.value = ids.map((id) => tags.value.find((tag) => tag.id === id)).filter(Boolean);
    }
});

let currentStep = ref(1);

// Step completion validation logic
const isStep1Complete = computed(() => {
    return selectedSource.value && selectedDestination.value;
});

const isStep2Complete = computed(() => {
    return (selectedSourceTreeNodes.value.length > 0 || getSelectedTransformTreeNodes().length > 0) && correctTreeSelection.value;
});

const isStep3Complete = computed(() => {
    return !!selectedTags.value.length && pipelineName.value;
});

// Can navigate to step logic
const canNavigateToStep = (stepIndex) => {
    if (stepIndex === 1) return true;
    if (stepIndex === 2) return isStep1Complete.value;
    if (stepIndex === 3) return isStep1Complete.value && isStep2Complete.value;
    return false;
};

const steps = computed(() => {
    return [
        {
            id: 'Step 1',
            index: 1,
            name: 'Select Connectors',
            complete: isStep1Complete.value,
            disabled: false
        },
        {
            id: 'Step 2',
            index: 2,
            name: 'Schema & Transforms',
            complete: isStep2Complete.value,
            disabled: !canNavigateToStep(2)
        },
        {
            id: 'Step 3',
            index: 3,
            name: 'Settings & Save',
            complete: isStep3Complete.value,
            disabled: !canNavigateToStep(3)
        }
    ];
});

let pipelineName = ref(null);
let selectedSource = ref(null);
let selectedDestination = ref(null);
let selectedSourceTreeNodes = ref([]);
let selectedSourceTopics = ref([]);

let sourceSearchQuery = ref('');
let destinationSearchQuery = ref('');

let saveLoading = ref(false);
const sourcesLoading = ref(false);
const destinationsLoading = ref(false);
const schemaLoading = ref(false);
const transformsLoading = ref(false);

let sourceConfig = ref();
let treeLevels = ref([]);
let treeNodes = ref([]);
let searcheableTreeNodes = ref([]);
let correctTreeSelection = ref(true);
let hidePromptTemporary = ref(false);

let transformTreeNodes = ref([]);
let transformTreeLevels = ref([]);

let snapshotNewTables = ref(true);

const nextEnabled = computed(() => {
    if (currentStep.value === 1) return isStep1Complete.value;
    if (currentStep.value === 2) return isStep2Complete.value;
    return false;
});

const filteredSources = computed(() => {
    if (!sourceSearchQuery.value.trim()) {
        return sources.value;
    }
    const query = sourceSearchQuery.value.toLowerCase();
    return sources.value.filter(
        (source) =>
            source.name.toLowerCase().includes(query) ||
            source.connector.toLowerCase().includes(query) ||
            getConnectorDisplayName(source.connector).toLowerCase().includes(query)
    );
});

const filteredDestinations = computed(() => {
    if (!destinationSearchQuery.value.trim()) {
        return destinations.value;
    }
    const query = destinationSearchQuery.value.toLowerCase();
    return destinations.value.filter(
        (destination) =>
            destination.name.toLowerCase().includes(query) ||
            destination.connector.toLowerCase().includes(query) ||
            getConnectorDisplayName(destination.connector).toLowerCase().includes(query)
    );
});

const canSave = computed(() => {
    return (
        !!selectedTags.value.length &&
        pipelineName.value &&
        (selectedSourceTreeNodes.value.length > 0 || getSelectedTransformTreeNodes().length > 0) &&
        correctTreeSelection.value
    );
});

onBeforeRouteLeave((to, from, next) => {
    if (sourcesLoading.value || destinationsLoading.value || transformsLoading.value) {
        return next();
    }

    if (currentStep.value === 1 || hidePromptTemporary.value) {
        return next();
    }

    const answer = window.confirm('Are you sure you want to leave? You may have unsaved changes.');

    if (answer) {
        return next();
    } else {
        return next(false);
    }
});

onMounted(async () => {
    try {
        sourcesLoading.value = true;
        destinationsLoading.value = true;
        transformsLoading.value = true;
        tagsLoading.value = true;

        await Promise.allSettled([
            tagsStore.fetch().then(() => {
                tags.value = tagsStore.tagsPerTypeForEntityCRUD[TAG_TYPES.PIPELINE];

                tagsLoading.value = false;
            }),
            sourcesStore.fetchAllBrief(false).then(() => {
                sources.value = _.cloneDeep(sourcesStore.allBrief);
                sourcesLoading.value = false;
            }),
            destinationsStore.fetchAllBrief(false).then(() => {
                destinations.value = _.cloneDeep(destinationsStore.allBrief);
                destinationsLoading.value = false;
            }),
            transformsStore.fetchForConnectors().then(() => {
                if (transformsStore.allForConnectors && !_.isEmpty(transformsStore.allForConnectors)) {
                    constructTransformTreeNodes();
                }

                transformsLoading.value = false;
            })
        ]);
    } finally {
        sourcesLoading.value = false;
        destinationsLoading.value = false;
        transformsLoading.value = false;
    }
});

const prevStep = () => {
    currentStep.value--;

    if (currentStep.value <= 1) {
        selectedTags.value = [];
    }
};

const nextStep = () => {
    currentStep.value++;
};

const createPipeline = async () => {
    saveLoading.value = true;

    if (!selectedTags.value?.length) {
        toast.error('Please select at least one tag.');
        saveLoading.value = false;
        return;
    }

    if (!selectedTags.value.find((tag) => tag.type?.includes(TAG_TYPES.ENVIRONMENT))) {
        toast.error('Please select at least one environment-related tag (Production/Development).');
        saveLoading.value = false;
        return;
    }

    processTreeNodesTopics(selectedSourceTreeNodes.value);
    hidePromptTemporary.value = true;
    try {
        const data = await pipelinesStore.create({
            name: pipelineName.value,
            snapshot_new_tables: !!snapshotNewTables.value,
            destination: {
                connector: selectedDestination.value.connector,
                name: selectedDestination.value.name,
                id: selectedDestination.value.id
            },
            source: {
                connector: selectedSource.value.connector,
                name: selectedSource.value.name,
                topics: selectedSourceTopics.value,
                id: selectedSource.value.id
            },
            transforms: getSelectedTransformTreeNodes(),
            tags: selectedTags.value.map((tag) => tag.id || tag._id)
        });

        toast.success('Pipeline created successfully.');
        if (data?.id) {
            router.push({
                name: 'pipeline-details',
                params: { id: data.id },
                query: {
                    source: selectedSource.value.id || data?.source?.id,
                    destination: selectedDestination.value.id || data?.destination?.id
                }
            });
        } else {
            router.push({ name: 'pipelines' });
        }
    } catch (err) {
        console.error(err);
        formError.value = err;
        hidePromptTemporary.value = false;
    } finally {
        saveLoading.value = false;
    }
};

const processTreeNodesTopics = (nodes, prefix = '') => {
    nodes.forEach((obj) => {
        const fullName = prefix ? `${prefix}.${obj.name}` : obj.name;

        if (!obj.children || obj.children?.length === 0) {
            if (obj.schemaLevel == treeLevels.value[treeLevels.value.length - 1].schemaLevel && obj.isSelected) {
                selectedSourceTopics.value.push(fullName);
            }
        } else {
            processTreeNodesTopics(obj.children, fullName);
        }
    });
};

const updateCorrectTreeSelectionState = (newValue) => {
    correctTreeSelection.value = newValue;
};

const updateSelectedSource = async (newSource) => {
    selectedSource.value =
        selectedSource.value && selectedSource.value.id === newSource.id
            ? null
            : sourcesStore.sources?.find((x) => x.id == newSource.id) || newSource;

    if (!selectedSource.value) {
        selectedDestination.value = null;
    }
};

const constructTransformTreeNodes = () => {
    transformTreeLevels.value.push({ schemaLevel: 'schema', schemaObject: 'random' }, { schemaLevel: 'topic', schemaObject: 'random' });
    transformTreeNodes.value = [];
    const children = [];

    _.forEach(transformsStore.allForConnectors, (transform) => {
        children.push({
            id: transform.id,
            name: transform.topic,
            transformName: transform.name,
            isSelected: false,
            schemaLevel: 'topic',
            schemaObject: transform
        });
    });

    transformTreeNodes.value.push({
        name: 'transforms',
        isSelected: false,
        collapsed: false,
        schemaLevel: 'schema',
        schemaObject: 'random',
        children
    });
};

const getSelectedTransformTreeNodes = () => {
    if (transformTreeNodes.value.length === 0) {
        return [];
    }
    return transformTreeNodes.value[0].children.filter((topicNode) => topicNode.isSelected).map((topicNode) => topicNode.schemaObject);
};

const updateSelectedDestination = (newDestination) => {
    selectedDestination.value =
        selectedDestination.value && selectedDestination.value.id === newDestination.id
            ? null
            : destinationsStore.destinations?.find((x) => x.id == newDestination.id) || newDestination;
};

const checkAllSelectedHaveSelectedChild = (nodes) => {
    for (let obj of nodes) {
        if (obj.isSelected && obj.children && obj.children?.length > 0) {
            if (!obj.children?.some((child) => child.isSelected)) {
                return false;
            }
        }

        if (obj.isSelected && (!obj.children || !obj.children?.length)) {
            return true;
        }

        if (obj.children) {
            const childResult = checkAllSelectedHaveSelectedChild(obj.children);
            if (!childResult) {
                return false;
            }
        }
    }
    return true;
};

const constructTreeNodes = async () => {
    let configProps = selectedSource.value.config;

    if (!configProps) {
        const [source] = await Promise.all([sourcesStore.getById(selectedSource.value.id), sourcesStore.fetchConnectors()]);

        configProps = source.config;
    }

    const sourceConnector = Object.values(sourcesStore.sourceConnectors)
        .flat()
        .find((x) => x.connector == selectedSource.value.connector);

    const nodes = buildTreeNodesOutOfExistingConfig(configProps, sourceConfig.value, sourceConnector, false);

    treeLevels.value = nodes.treeLevels;
    treeNodes.value = nodes.treeNodes;

    return { nodes: treeNodes.value, levels: treeLevels.value };
};

const handleTagSelection = () => {
    const existingExclusiveTag = selectedTags.value.filter((tag) => tag.type?.length && MAX_ONE_TAG_PER_TYPE.some((type) => tag.type.includes(type)));

    if (existingExclusiveTag?.length > 1) {
        toast.warning(`Only one tag of the same ${MAX_ONE_TAG_PER_TYPE.join(', ')} type can be selected`);

        selectedTags.value = selectedTags.value.filter((tag) => tag.type?.length && !MAX_ONE_TAG_PER_TYPE.some((type) => tag.type.includes(type)));
        selectedTags.value.push(existingExclusiveTag[0]);
        return;
    }
};

// Watch for changes in selectedTags to handle tag selection validation
watch(
    selectedTags,
    () => {
        handleTagSelection();
    },
    { deep: true }
);

watch(
    treeNodes,
    (newVal) => {
        selectedSourceTreeNodes.value = newVal.filter((x) => x.isSelected);
        // Only validate schema tree if schema nodes are actually selected
        // If only transforms are selected (no schema), validation should pass
        if (selectedSourceTreeNodes.value.length > 0) {
            correctTreeSelection.value = checkAllSelectedHaveSelectedChild(selectedSourceTreeNodes.value);
        } else {
            // No schema selected - validation passes (transforms don't require parent-child validation)
            correctTreeSelection.value = true;
        }
    },
    { deep: true }
);

// Watch transform tree to update validation when transforms change
watch(
    transformTreeNodes,
    () => {
        // If no schema is selected, ensure validation reflects transform selection
        if (selectedSourceTreeNodes.value.length === 0) {
            correctTreeSelection.value = true; // Transforms don't need parent-child validation
        }
    },
    { deep: true }
);

watch(selectedDestination, (newVal) => {
    formError.value = null;
    pipelineName.value = `${selectedSource.value?.name} __ ${newVal?.name} __ pipeline`;
});

watch(
    selectedSource,
    async (newVal) => {
        if (newVal) {
            schemaLoading.value = true;
            formError.value = null;

            try {
                await Promise.allSettled([
                    sourcesStore.fetchConfiguration(selectedSource.value.connector).then(async () => {
                        sourceConfig.value = sourcesStore.configurations[selectedSource.value.connector];

                        if (sourceConfig.value) {
                            await constructTreeNodes();

                            searcheableTreeNodes.value = Object.assign([], treeNodes.value);
                        }

                        schemaLoading.value = false;
                    })
                ]);
            } finally {
                schemaLoading.value = false;
            }
        }
    },
    { deep: true }
);

watch([destinationSearchQuery, sourceSearchQuery], ([destinationQuery, sourceQuery]) => {
    if (sourceQuery) {
        sources.value = _.cloneDeep(sourcesStore.allBrief.filter((x) => x.name.toLowerCase().includes(sourceQuery.toLowerCase())));
    } else {
        sources.value = _.cloneDeep(sourcesStore.allBrief);
    }

    if (destinationQuery) {
        destinations.value = _.cloneDeep(destinationsStore.allBrief.filter((x) => x.name.toLowerCase().includes(destinationQuery.toLowerCase())));
    } else {
        destinations.value = _.cloneDeep(destinationsStore.allBrief);
    }
});

// watch(
//     searchQuery,
//     (newVal) => {
//         if (newVal.length >= 3) {
//             searcheableTreeNodes.value = Object.assign(
//                 [],
//                 treeNodes?.value.filter((x) => x.name.toLowerCase().includes(newVal.toLocaleLowerCase()))
//             );
//         }

//         if (!newVal) {
//             loadTreeData();
//         }
//     },
//     { deep: true }
// );

// const loadTreeData = () => {
//     treeNodes.value = [];
//     selectedSource.value.topics.forEach((topic) => {
//         treeNodes.value.push({
//             name: topic,
//             isSelected: true,
//             schemaLevel: 'topic',
//             schemaObject: 'random',
//             children: []
//         });
//     });

//     searcheableTreeNodes.value = Object.assign([], treeNodes.value);
// };
</script>

<template>
    <div>
        <div class="page-header">
            <PageBreadcrumb />
        </div>
        <div>
            <div v-if="sourcesLoading || destinationsLoading || (sourcesStore.allBrief?.length && destinationsStore.allBrief?.length)">
                <Card class="mb-6">
                    <CardContent>
                        <Stepper v-model="currentStep" orientation="horizontal" class="w-full">
                            <StepperItem v-for="step in steps" :key="step.index" :step="step.index" class="flex-1">
                                <StepperTrigger
                                    :disabled="step.disabled"
                                    :class="[
                                        'flex flex-col items-center gap-2 p-2 w-full',
                                        !step.disabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                                    ]"
                                    @click="!step.disabled ? (currentStep = step.index) : null">
                                    <StepperIndicator :complete="step.complete">
                                        {{ step.index }}
                                    </StepperIndicator>
                                    <StepperTitle class="text-sm font-medium">
                                        {{ step.name }}
                                    </StepperTitle>
                                </StepperTrigger>
                                <StepperSeparator v-if="step.index < steps.length" />
                            </StepperItem>
                        </Stepper>
                    </CardContent>
                </Card>
                <div class="mt-8">
                    <div v-if="currentStep == 1" class="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
                        <div class="w-full flex flex-col">
                            <div class="flex flex-col gap-3 mb-3">
                                <div class="font-semibold text-lg flex items-center">
                                    <CloudDownload class="h-5 w-5 mr-2" />
                                    Select a Source
                                    <Badge v-if="selectedSource" variant="secondary" class="ml-2"> Selected </Badge>
                                </div>
                                <div class="w-full flex justify-start">
                                    <SearchInput
                                        :loading="sourcesLoading || saveLoading"
                                        :custom-class="'w-full pr-3'"
                                        :placeholder="'Search sources...'"
                                        @search="(input) => (sourceSearchQuery = input)" />
                                </div>
                            </div>

                            <ScrollArea class="h-58-dynamic">
                                <div class="space-y-3 pr-4">
                                    <!-- Skeleton loading state -->
                                    <template v-if="sourcesLoading">
                                        <div v-for="n in 6" :key="`source-skeleton-${n}`" class="relative">
                                            <Card class="transition-all">
                                                <CardContent class="flex items-center gap-3">
                                                    <SkeletonLoader type="circle" class="h-10 w-10" :loading="true" />
                                                    <div class="flex-1">
                                                        <SkeletonLoader class="h-5 w-48" :loading="true" />
                                                        <SkeletonLoader class="h-4 w-32 mt-1" :loading="true" />
                                                    </div>
                                                    <SkeletonLoader class="h-6 w-20" :loading="true" />
                                                </CardContent>
                                            </Card>
                                        </div>
                                    </template>

                                    <!-- Actual source data -->
                                    <template v-else>
                                        <!-- Add new source card -->
                                        <Card
                                            class="group cursor-pointer transition-colors hover:bg-accent border-l-4 border-l-primary"
                                            @click="router.push({ name: 'add-connectors', query: { tab: 'Sources' } })">
                                            <CardContent class="flex items-center gap-3">
                                                <div class="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                    <PlusCircle class="h-5 w-5 text-primary" />
                                                </div>
                                                <div class="flex-1">
                                                    <h3 class="font-medium group-hover:text-primary transition-colors">Add a new source</h3>
                                                </div>
                                                <ChevronRight class="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                            </CardContent>
                                        </Card>

                                        <!-- Source connectors -->
                                        <div v-for="(source, index) in filteredSources" :key="source.id || index" class="relative">
                                            <Card
                                                class="group cursor-pointer transition-colors"
                                                :class="{
                                                    'hover:bg-accent': selectedSource?.id !== source.id,
                                                    'bg-primary/5 border-primary': selectedSource?.id === source.id
                                                }"
                                                @click="updateSelectedSource(source)">
                                                <CardContent class="flex items-center gap-3">
                                                    <Avatar
                                                        class="h-10 w-10 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                                                        <AvatarImage
                                                            :src="getConnectorIcon(source.connector) || `/icons/${source.connector}.svg`"
                                                            :alt="source.connector"
                                                            class="object-contain p-1" />
                                                        <AvatarFallback
                                                            class="bg-muted dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-md">
                                                            {{ source.connector.charAt(0).toUpperCase() }}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div class="flex-1 min-w-0">
                                                        <h3
                                                            class="font-medium truncate transition-colors"
                                                            :class="{
                                                                'group-hover:text-primary': selectedSource?.id !== source.id,
                                                                'text-primary': selectedSource?.id === source.id
                                                            }">
                                                            {{ source.name }}
                                                        </h3>
                                                    </div>
                                                    <Badge variant="secondary" class="shrink-0">
                                                        {{ getConnectorDisplayName(source.connector) }}
                                                    </Badge>
                                                    <ChevronRight
                                                        class="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors"
                                                        :class="{ 'text-primary': selectedSource?.id === source.id }" />
                                                </CardContent>
                                            </Card>
                                        </div>

                                        <!-- Empty state -->
                                        <Card v-if="!filteredSources?.length" class="border-dashed">
                                            <CardContent class="flex flex-col items-center justify-center p-6 text-center">
                                                <Database class="h-8 w-8 text-muted-foreground mb-3" />
                                                <h3 class="font-medium mb-2">No sources found</h3>
                                                <p class="text-sm text-muted-foreground mb-3">Try adjusting your search or add a new source</p>
                                                <Button variant="outline" size="sm" @click="sourceSearchQuery = ''">Clear search</Button>
                                            </CardContent>
                                        </Card>
                                    </template>
                                </div>
                                <ScrollBar orientation="vertical" />
                            </ScrollArea>
                        </div>

                        <div class="w-full flex flex-col">
                            <div class="flex flex-col gap-3 mb-3">
                                <div class="font-semibold text-lg flex items-center">
                                    <CloudUpload class="h-5 w-5 mr-2" />
                                    Select a Destination
                                    <Badge v-if="selectedDestination" variant="secondary" class="ml-2"> Selected </Badge>
                                </div>
                                <div class="w-full flex justify-start">
                                    <SearchInput
                                        :loading="destinationsLoading || saveLoading"
                                        :custom-class="'w-full pr-3'"
                                        :placeholder="'Search destinations...'"
                                        @search="(input) => (destinationSearchQuery = input)" />
                                </div>
                            </div>
                            <ScrollArea class="h-58-dynamic">
                                <div class="space-y-3 pr-4">
                                    <!-- Skeleton loading state -->
                                    <template v-if="destinationsLoading">
                                        <div v-for="n in 6" :key="`destination-skeleton-${n}`" class="relative">
                                            <Card class="transition-all">
                                                <CardContent class="flex items-center gap-3">
                                                    <SkeletonLoader type="circle" class="h-10 w-10" :loading="true" />
                                                    <div class="flex-1">
                                                        <SkeletonLoader class="h-5 w-48" :loading="true" />
                                                        <SkeletonLoader class="h-4 w-32 mt-1" :loading="true" />
                                                    </div>
                                                    <SkeletonLoader class="h-6 w-20" :loading="true" />
                                                </CardContent>
                                            </Card>
                                        </div>
                                    </template>

                                    <!-- Actual destination data -->
                                    <template v-else>
                                        <!-- Add new destination card -->
                                        <Card
                                            class="group cursor-pointer transition-colors hover:bg-accent border-l-4 border-l-primary"
                                            @click="
                                                router.push({
                                                    name: 'add-connectors',
                                                    query: { tab: 'Destinations' }
                                                })
                                            ">
                                            <CardContent class="flex items-center gap-3">
                                                <div class="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                    <PlusCircle class="h-5 w-5 text-primary" />
                                                </div>
                                                <div class="flex-1">
                                                    <h3 class="font-medium group-hover:text-primary transition-colors">Add a new destination</h3>
                                                </div>
                                                <ChevronRight class="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                            </CardContent>
                                        </Card>

                                        <!-- Destination connectors -->
                                        <div v-for="(destination, index) in filteredDestinations" :key="destination.id || index" class="relative">
                                            <Card
                                                class="group transition-colors"
                                                :class="{
                                                    'cursor-pointer hover:bg-accent': selectedSource && selectedDestination?.id !== destination.id,
                                                    'cursor-pointer bg-primary/5 border-primary':
                                                        selectedSource && selectedDestination?.id === destination.id,
                                                    'cursor-not-allowed opacity-50': !selectedSource
                                                }"
                                                @click="selectedSource && updateSelectedDestination(destination)">
                                                <CardContent class="flex items-center gap-3">
                                                    <Avatar
                                                        class="h-10 w-10 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                                                        <AvatarImage
                                                            :src="getConnectorIcon(destination.connector) || `/icons/${destination.connector}.svg`"
                                                            :alt="destination.connector"
                                                            class="object-contain p-1" />
                                                        <AvatarFallback
                                                            class="bg-muted dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-md">
                                                            {{ destination.connector.charAt(0).toUpperCase() }}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div class="flex-1 min-w-0">
                                                        <h3
                                                            class="font-medium truncate transition-colors"
                                                            :class="{
                                                                'group-hover:text-primary':
                                                                    selectedSource && selectedDestination?.id !== destination.id,
                                                                'text-primary': selectedSource && selectedDestination?.id === destination.id,
                                                                'text-muted-foreground': !selectedSource
                                                            }">
                                                            {{ destination.name }}
                                                        </h3>
                                                        <p v-if="!selectedSource" class="text-xs text-muted-foreground truncate">
                                                            Select a source first
                                                        </p>
                                                    </div>
                                                    <Badge variant="secondary" class="shrink-0" :class="{ 'opacity-50': !selectedSource }">
                                                        {{ getConnectorDisplayName(destination.connector) }}
                                                    </Badge>
                                                    <ChevronRight
                                                        class="h-4 w-4 transition-colors"
                                                        :class="{
                                                            'text-muted-foreground group-hover:text-primary':
                                                                selectedSource && selectedDestination?.id !== destination.id,
                                                            'text-primary': selectedSource && selectedDestination?.id === destination.id,
                                                            'text-muted-foreground/50': !selectedSource
                                                        }" />
                                                </CardContent>
                                            </Card>
                                        </div>

                                        <!-- Empty state -->
                                        <Card v-if="!filteredDestinations?.length" class="border-dashed">
                                            <CardContent class="flex flex-col items-center justify-center p-6 text-center">
                                                <Database class="h-8 w-8 text-muted-foreground mb-3" />
                                                <h3 class="font-medium mb-2">No destinations found</h3>
                                                <p class="text-sm text-muted-foreground mb-3">Try adjusting your search or add a new destination</p>
                                                <Button variant="outline" size="sm" @click="destinationSearchQuery = ''">Clear search</Button>
                                            </CardContent>
                                        </Card>
                                    </template>
                                </div>
                                <ScrollBar orientation="vertical" />
                            </ScrollArea>
                        </div>
                    </div>
                    <div v-if="currentStep == 2" class="w-full">
                        <Card>
                            <CardContent class="space-y-8">
                                <div class="flex flex-col gap-4">
                                    <Label class="text-base font-medium"> {{ selectedSource.name }} - Schema Selection </Label>
                                    <Card>
                                        <CardContent>
                                            <!-- Schema TreeView -->
                                            <TreeView
                                                :tree-nodes="searcheableTreeNodes || []"
                                                :tree-levels="treeLevels || []"
                                                :loading="schemaLoading"
                                                :allow-search="true"
                                                :disable-create-and-edit="true"
                                                :custom-width="'100%'"
                                                :edit-mode="true"
                                                class="w-full"
                                                @can-save-duplicate="updateCorrectTreeSelectionState" />

                                            <div
                                                v-if="
                                                    !schemaLoading &&
                                                    (!searcheableTreeNodes || !treeLevels || !treeLevels[0] || !treeLevels[0].schemaLevel)
                                                "
                                                class="flex flex-col items-center justify-center py-8 text-center">
                                                <Search class="h-8 w-8 text-muted-foreground mb-3" />
                                                <p class="text-sm text-muted-foreground">No schema available</p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>

                                <div class="flex flex-col gap-4">
                                    <Label class="text-base font-medium"> Transforms Selection </Label>
                                    <Card>
                                        <CardContent>
                                            <!-- Transforms TreeView - Group by 'id' at depth 1 (topic level) -->
                                            <TreeView
                                                :tree-nodes="transformTreeNodes || []"
                                                :tree-levels="transformTreeLevels || []"
                                                :loading="transformsLoading"
                                                class="w-full"
                                                :custom-width="'100%'"
                                                :allow-search="true"
                                                :disable-create-and-edit="true"
                                                :edit-mode="true"
                                                :group-by="{ level: 1, key: 'id', labelKey: 'transformName' }"
                                                :group-prefix="''" />
                                            <div
                                                v-if="!transformsLoading && (!transformTreeNodes || !transformTreeLevels || !transformTreeLevels[0])"
                                                class="flex flex-col items-center justify-center py-8 text-center">
                                                <Search class="h-8 w-8 text-muted-foreground mb-3" />
                                                <p class="text-sm text-muted-foreground">No transforms found</p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    <div v-if="currentStep == 3" class="w-full">
                        <Card>
                            <CardContent class="space-y-6">
                                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    <div class="lg:col-span-1">
                                        <Label for="pipeline-name" class="text-base font-medium">Pipeline Name</Label>
                                    </div>
                                    <div class="lg:col-span-2">
                                        <!-- Pipeline name loading skeleton -->
                                        <template v-if="!selectedSource || !selectedDestination">
                                            <SkeletonLoader class="h-9 w-full" :loading="true" />
                                        </template>
                                        <!-- Actual pipeline name input -->
                                        <template v-else>
                                            <Input
                                                type="text"
                                                v-model="pipelineName"
                                                placeholder="Enter pipeline name"
                                                required
                                                name="pipeline-name"
                                                id="pipeline-name"
                                                autocomplete="given-name"
                                                :disabled="saveLoading"
                                                v-sanitize
                                                v-sanitize-csv />
                                        </template>
                                    </div>
                                </div>
                                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    <div class="lg:col-span-1">
                                        <Label class="text-base font-medium flex items-center">
                                            Tags
                                            <a
                                                href="https://docs.streamkap.com/docs/streamkap-support"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                class="ml-2 text-muted-foreground hover:text-primary transition-colors">
                                                <HelpCircle class="w-4 h-4" />
                                            </a>
                                        </Label>
                                    </div>
                                    <div class="lg:col-span-2">
                                        <!-- Tags loading skeleton -->
                                        <template v-if="tagsLoading">
                                            <div class="space-y-3">
                                                <div v-for="n in 2" :key="`tags-skeleton-${n}`" class="flex items-center gap-3">
                                                    <SkeletonLoader type="circle" class="h-6 w-6" :loading="true" />
                                                    <div class="flex-1">
                                                        <SkeletonLoader class="h-4 w-32" :loading="true" />
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                        <!-- Actual tags multiselect -->
                                        <template v-else>
                                            <MultiSelect
                                                v-model="selectedTagIds"
                                                :options="tags"
                                                :placeholder="tags?.length ? 'Select tags' : 'No tags available'"
                                                :option-label="'name'"
                                                :track-by="'id'"
                                                :class="{
                                                    'border-destructive': !selectedTags.length && currentStep === 3
                                                }" />
                                            <p v-if="!selectedTags.length && currentStep === 3" class="text-sm text-destructive mt-1">
                                                At least one tag is required
                                            </p>
                                        </template>
                                    </div>
                                </div>

                                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    <div class="lg:col-span-1">
                                        <Label class="text-base font-medium">Snapshot topics</Label>
                                    </div>
                                    <div class="lg:col-span-2 flex items-center gap-3">
                                        <Switch v-model="snapshotNewTables" :disabled="transformsLoading || schemaLoading" />
                                        <LoaderCircle v-if="transformsLoading || schemaLoading" class="h-4 w-4 animate-spin" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
            <div v-else class="border-l-4 border-destructive bg-destructive/5 p-4 mt-[100px]">
                <div class="flex">
                    <div class="shrink-0">
                        <AlertTriangle class="h-5 w-5 text-destructive" />
                    </div>
                    <div class="ml-3">
                        <p class="text-xs text-destructive">
                            Connectors are <span class="underline">required</span> before creating a new Pipeline.
                            {{ ' ' }}
                        </p>
                        <p class="text-xs text-destructive">Create at least 1 Source and 1 Destination.</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Footer will teleport to App.vue footer slot -->
        <AppFooter>
            <template #right>
                <div v-if="formError" class="flex flex-col text-md font-semibold items-end mr-10">
                    <ErrorMessage
                        :error="formError"
                        :to-sentry="true"
                        :context="'create-pipeline'"
                        :introduction="'An error occurred while creating your pipeline.'" />
                </div>

                <ActionMenu
                    variant="row"
                    :show-save="true"
                    :show-next="true"
                    :show-back="true"
                    :show-discard="false"
                    :disable-back="currentStep <= 1"
                    :disable-next="!nextEnabled"
                    :disable-save="
                        !canSave ||
                        saveLoading ||
                        sourcesLoading ||
                        destinationsLoading ||
                        !(sourcesStore.allBrief?.length && destinationsStore.allBrief?.length)
                    "
                    :loading="saveLoading"
                    @next="nextStep"
                    @back="prevStep"
                    @save="createPipeline" />
            </template>
        </AppFooter>
    </div>
</template>

<style>
.step-container {
    display: flex;
    flex-direction: column;
}

.step {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.disabled-toggle {
    cursor: not-allowed;
    opacity: 0.5;
}
</style>
